import React, { FC, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Button from "@mui/material/Button";
import useApi from "../../../../hooks/useApi";
import usePifSettings from "../../../../hooks/usePifSettings";
import { useToaster } from "../../../../hooks/useToaster";
import useContainerWidth from "../../../../hooks/useContainerWidth";
// postIfIdentityLookup replaces Epic 79822's `lookupEmployee`, which read the stub
// fixtures this epic removed.
import {
  emailHrDirectorApprovalRequest,
  emailHrDoNotRehireContact,
  postIfIdentityLookup,
} from "../../../../services/pifService";
import { PostIfIdentityLookupRequestDto, PostIfIdentityLookupResultDto } from "../../../../types/pifDto";
import { SEVERITIES } from "../../../../data/constants";
import { HAMASPIK_COLORS } from "../../../../assets/hamaspik/theme";
import Badge from "../../../../assets/hamaspik/Badge";
import { mapIdentityLookupResult } from "../pifLookup";
import { PifCandidate, PifFlow } from "../types";
import "./PifStartScreen.scss";

interface PifStartScreenProps {
  /** Creates the draft, so it is async — the screen stays put until it resolves. */
  onConfirmed: (flow: PifFlow, candidate: PifCandidate | null, lookupValue?: string) => void | Promise<void>;
  /**
   * Called instead of onConfirmed when the lookup found an open draft this user already owns,
   * so the same SSN reopens that form with its saved answers rather than creating a duplicate.
   */
  onResume?: (postIfId: number, candidate: PifCandidate | null) => void;
  /** True while the draft is being created, so the confirm actions can't double-fire. */
  opening?: boolean;
}

const CHAIN_PREVIEW = [
  // "Supervisor", not "Your Hiring Supervisor": the preview names the same four signing roles the
  // approval chain does (plus the Completed stage, Epic 82838), and this step had drifted to a different wording for the creator (Bug 82350).
  { n: "1", name: "Supervisor", detail: "Completes and submits the form" },
  { n: "2", name: "Department Director", detail: "Reviews and approves the request" },
  { n: "3", name: "HR Director", detail: "Reviews HR details and approves the hire" },
  { n: "4", name: "Executive Director", detail: "Provides the final approval" },
  // Epic 82838: the fifth lifecycle stage, matching the list and every chain after submit.
  { n: "5", name: "Completed", detail: "HR matches the form to an employee" },
];

/** SSN is complete at 9 digits; Employee ID is 1–6 (wait for a pause so mid-SSN typing doesn't fire early). */
const EMP_ID_DEBOUNCE_MS = 650;
/**
 * Below this length the input is far more likely to be an SSN being typed than a finished Employee
 * ID, so it waits longer still — 650ms is short enough that a slow typist trips a lookup at 1, 2
 * and 3 digits on the way to a 9-digit SSN.
 */
const SHORT_INPUT_MAX_DIGITS = 3;
const SHORT_INPUT_DEBOUNCE_MS = 1500;
/** Brief confirmation flash before opening the form — wrong-person escape hatch. */
const MATCH_FLASH_MS = 1500;

/** Width at which the chain-preview rail moves beside the form instead of below it. */
const START_WIDE_COL = 1024;

/** Employee ID stays plain (≤6 digits); SSN gets XXX-XX-XXXX as digits accumulate past 6. */
const formatLookupInput = (raw: string): string => {
  const d = raw.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 6) return d;
  const a = d.slice(0, 3);
  const b = d.slice(3, 5);
  const c = d.slice(5, 9);
  return `${a}-${b}-${c}`;
};

const PifStartScreen: FC<PifStartScreenProps> = ({ onConfirmed, onResume, opening = false }) => {
  const { executeApi } = useApi();
  const { retryLimit } = usePifSettings();
  const showToaster = useToaster();
  // Disables the "Email HR Director" button for the round trip, so a double-click cannot put two
  // identical requests in HR inbox.
  const [sending, setSending] = useState(false);
  const [rootRef, width] = useContainerWidth<HTMLDivElement>();
  const [pre, setPre] = useState<"yes" | "no" | null>(null);
  const [q, setQ] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [matched, setMatched] = useState<PifCandidate | null>(null);
  const [dnr, setDnr] = useState(false);
  const [dnrMessage, setDnrMessage] = useState("");
  // The blocked person's employee id, for the card's "Contact HR" email (Bug 82168). Null when the
  // API did not supply one, which disables the button rather than emailing HR about nobody.
  const [dnrEmployeeId, setDnrEmployeeId] = useState<number | null>(null);
  // Separate from `sending` above: the "Email HR Director" button is not hidden while the blocked
  // card is up, so one shared flag would disable whichever button the user was not clicking.
  const [contactingHr, setContactingHr] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [flashPaused, setFlashPaused] = useState(false);
  // Set by the lookup when this person already has one of the user's open drafts; consumed by
  // openMatchedCandidate. A ref, not state, so it never re-renders the match card.
  const resumeDraftId = useRef<number | null>(null);
  const lookupSeq = useRef(0);
  const flashTimer = useRef<number | null>(null);
  const flashRemaining = useRef(MATCH_FLASH_MS);
  const flashStartedAt = useRef(0);

  const digits = q.replace(/\D/g, "");
  // An Employee ID can be a single digit, so anything shorter than a full SSN reads as one.
  const detected = digits.length >= 9 ? "READS AS SSN" : digits.length >= 1 ? "READS AS EMPLOYEE ID" : "";

  const clearFlashTimer = () => {
    if (flashTimer.current !== null) {
      window.clearTimeout(flashTimer.current);
      flashTimer.current = null;
    }
  };

  const resetLookupState = () => {
    clearFlashTimer();
    resumeDraftId.current = null;
    setMatched(null);
    setDnr(false);
    setDnrMessage("");
    setDnrEmployeeId(null);
    setNotFound(false);
  };

  /**
   * "Email HR Director" — asks HR for go-ahead before any paperwork starts. No PostIF exists yet,
   * so the request carries no id; the API resolves the recipient from the "HR Director User Id"
   * system setting and puts this supervisor's own name and email in the body (the mail is sent
   * from donotreply@, so that is HR's only way to reply to a person).
   *
   * `sending` blocks a double-click: the button stays disabled for the round trip, so an impatient
   * supervisor can't put two identical requests in HR's inbox. A 409 ("no HR Director configured")
   * is already surfaced as an error alert by useApi, which is why only success is toasted here.
   */
  const handleEmailHrDirector = async () => {
    setSending(true);
    try {
      const result = await executeApi(emailHrDirectorApprovalRequest, undefined);
      if (result) showToaster("Approval request sent to the HR Director.", SEVERITIES.SUCCESS);
    } finally {
      setSending(false);
    }
  };

  /**
   * "Contact HR" on the Do-Not-Rehire blocked card (Bug 82168) — the button previously rendered
   * with no handler at all. Sends the same HR Director mail as the button above, but on the
   * Do-Not-Rehire template, so it carries the employee id the mail identifies the person by.
   *
   * The id comes from the blocked lookup response, never from the value the supervisor typed:
   * that may be an SSN, which must not be emailed. If the API did not return one the button is
   * disabled, so this guard only covers a response that changes underneath an open card.
   *
   * `contactingHr` blocks a double-click for the round trip. A 409 (no HR Director configured) and
   * a 429 (rate limit) are already surfaced as error alerts by useApi, which is why only success
   * is toasted here.
   */
  const handleContactHr = async () => {
    if (dnrEmployeeId === null) return;
    setContactingHr(true);
    try {
      const result = await executeApi(emailHrDoNotRehireContact, dnrEmployeeId);
      if (result) showToaster("Sent to the HR Director. They'll follow up with you.", SEVERITIES.SUCCESS);
    } finally {
      setContactingHr(false);
    }
  };

  const openMatchedCandidate = (candidate: PifCandidate) => {
    // Resume beats create: the draft already holds this person's saved answers.
    if (resumeDraftId.current && onResume) {
      // Hand the candidate over too: it carries employeeId, and the reopened form cannot recover
      // it from the record when Portal_PostIFApplicants.EnterpriseEmployeeId is still NULL.
      onResume(resumeDraftId.current, candidate);
      return;
    }
    void onConfirmed(candidate.status === "active" ? "active" : "inactive", candidate);
  };

  const startMatchFlash = (candidate: PifCandidate) => {
    clearFlashTimer();
    setMatched(candidate);
    setDnr(false);
    setDnrMessage("");
    setDnrEmployeeId(null);
    setNotFound(false);
    setFlashPaused(false);
    flashRemaining.current = MATCH_FLASH_MS;
    flashStartedAt.current = Date.now();
    flashTimer.current = window.setTimeout(() => {
      flashTimer.current = null;
      openMatchedCandidate(candidate);
    }, MATCH_FLASH_MS);
  };

  /** Give keyboard/screen-reader users time to hit "Wrong person" instead of auto-advancing under them. */
  const pauseMatchFlash = () => {
    if (!matched || flashTimer.current === null) return;
    window.clearTimeout(flashTimer.current);
    flashTimer.current = null;
    flashRemaining.current = Math.max(0, flashRemaining.current - (Date.now() - flashStartedAt.current));
    setFlashPaused(true);
  };

  const resumeMatchFlash = () => {
    if (!matched || flashTimer.current !== null) return;
    setFlashPaused(false);
    flashStartedAt.current = Date.now();
    flashTimer.current = window.setTimeout(() => {
      flashTimer.current = null;
      openMatchedCandidate(matched);
    }, flashRemaining.current);
  };

  const handleMatchCardBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) resumeMatchFlash();
  };

  const rejectMatched = () => {
    clearFlashTimer();
    resumeDraftId.current = null;
    setMatched(null);
    setQ("");
    setAttempts((n) => n + 1);
    lookupSeq.current += 1;
  };

  const runLookup = async (query: string) => {
    const dig = query.replace(/\D/g, "");
    if (!dig) return;

    const seq = ++lookupSeq.current;
    setLookingUp(true);
    clearFlashTimer();
    resumeDraftId.current = null;
    setMatched(null);
    setDnr(false);
    setDnrMessage("");
    setDnrEmployeeId(null);
    setNotFound(false);

    // Send the digits only, never the formatted display value.
    const response = await executeApi<PostIfIdentityLookupRequestDto, PostIfIdentityLookupResultDto>(
      postIfIdentityLookup,
      { lookupValue: dig },
    );
    if (seq !== lookupSeq.current) return;
    setLookingUp(false);

    // Null means useApi already surfaced the failure (including a 429 rate-limit or a
    // 403 for a caller who may not hire) - don't guess at an outcome on top of it.
    if (!response) return;

    const result = mapIdentityLookupResult(response);
    resumeDraftId.current = result.kind === "match" ? (result.existingDraftPostIfId ?? null) : null;

    if (result.kind === "blocked") {
      setDnr(true);
      setDnrMessage(result.message);
      setDnrEmployeeId(result.employeeId ?? null);
      return;
    }
    if (result.kind === "new-applicant") {
      // An unmatched SSN / Employee ID goes straight into the form - no confirmation card. The
      // state below is still set so that if create-draft fails the supervisor lands back here
      // with the "New applicant" card rather than a blank screen.
      setNotFound(true);
      setAttempts((n) => n + 1);
      confirmNewApplicant();
      return;
    }
    startMatchFlash(result.candidate);
  };

  const handleQChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = formatLookupInput(e.target.value);
    // A 10th digit, a typed dash or a repeated autofill event formats to the same value. The
    // auto-lookup effect only re-runs when `q` changes, so cancelling here would drop the
    // in-flight lookup with nothing to replace it (Bug 82844).
    if (next === q) return;
    lookupSeq.current += 1;
    setLookingUp(false);
    setQ(next);
    resetLookupState();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && digits.length >= 1 && !lookingUp && !matched) {
      e.preventDefault();
      void runLookup(q);
    }
  };

  const handlePreChange = (_: React.MouseEvent<HTMLElement>, value: "yes" | "no" | null) => {
    if (value !== null) setPre(value);
  };

  // Auto-lookup: 9 digits (SSN) right away; 1–6 digits after a pause (Employee ID). The pause
  // is what keeps a part-typed SSN from firing on its way past the Employee ID lengths — the timer
  // restarts on every keystroke, and 7–8 digits are held below as too long for an ID, too short
  // for an SSN.
  //
  // The pause scales with how little has been typed: 1–3 digits is almost never a real Employee ID
  // being finished, it is someone mid-SSN, so those wait far longer before spending a lookup and
  // risking a "new applicant" flash the next keystroke invalidates (PR 30856 review, Pooja Pujara).
  useEffect(() => {
    if (pre !== "yes") return;
    if (digits.length < 1) return;
    if (matched) return;
    if (digits.length > 6 && digits.length < 9) return;

    const delay = digits.length >= 9 ? 0 : digits.length <= SHORT_INPUT_MAX_DIGITS ? SHORT_INPUT_DEBOUNCE_MS : EMP_ID_DEBOUNCE_MS;
    const timer = window.setTimeout(() => {
      void runLookup(q);
    }, delay);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, pre]);

  useEffect(() => () => clearFlashTimer(), []);

  const confirmNewApplicant = () => {
    // Carry the typed value into the form as the SSN only when it is a full nine digits - with no
    // candidate record that is the only place a new applicant's SSN arrives.
    //
    // Anything shorter was read as an Employee ID (the "READS AS EMPLOYEE ID" chip above) and then
    // matched no record, so it is a hiring number, not an SSN, and must not be handed over as one:
    // the Applicant section used to show it back as the SSN's last four in a field nobody could
    // correct (Bug 82668). Hand over nothing and let the supervisor type the real SSN there.
    void onConfirmed("new", null, digits.length === 9 ? q : "");
  };

  const matchedName = matched ? `${matched.first ?? ""} ${matched.last ?? ""}`.trim() : "";

  return (
    <Box className={`pif-start${width >= START_WIDE_COL ? " pif-start--wide" : ""}`} ref={rootRef}>
      <Box className="pif-start__main">
        {/* No eyebrow label here: the page band above the card already names the form. */}
        <Box className="pif-start__hero">
          <Typography className="pif-start__title">
            You&apos;ve made an offer.
            <br />
            <span className="pif-start__title-accent">Let&apos;s make it official.</span>
          </Typography>
          <Typography className="pif-start__lede">
            The Post Interview Form initiates the hiring process. Confirm HR Director approval, identify the employee
            you&apos;re hiring, and we&apos;ll tailor the form accordingly.
          </Typography>
        </Box>

        <Box className="pif-start__step">
          <Box className="pif-start__step-row">
            <SectionDot done={pre === "yes"} mark="1" />
            <Typography className="pif-start__step-question">Do you have HR Director approval for this hire?</Typography>
          </Box>
          <Box className="pif-start__step-indent">
            <ToggleButtonGroup
              exclusive
              value={pre}
              onChange={handlePreChange}
              size="small"
              aria-label="HR Director approval"
              sx={{
                bgcolor: HAMASPIK_COLORS.neutral100,
                borderRadius: "12px",
                p: 0.5,
                gap: 0.5,
                "& .MuiToggleButtonGroup-grouped": {
                  border: 0,
                  borderRadius: "10px !important",
                  px: 2.5,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: HAMASPIK_COLORS.textMuted,
                  "&.Mui-selected": {
                    bgcolor: "background.paper",
                    color: HAMASPIK_COLORS.heading,
                    boxShadow: "0 1px 4px rgba(29,12,12,0.1)",
                    "&:hover": { bgcolor: "background.paper" },
                  },
                },
              }}
            >
              <ToggleButton value="yes" data-testid="postif-gate-preapproved-yes">
                Yes
              </ToggleButton>
              <ToggleButton value="no" data-testid="postif-gate-preapproved-not-yet">
                Not yet
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>        
        </Box>

        {pre === "no" && (
          <Alert severity="warning" className="pif-start__alert">
            <Typography fontWeight={700} sx={{ mb: 0.5 }}>
              Talk to HR first
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Every hire needs the HR Director&apos;s go-ahead before the paperwork starts.
            </Typography>
            <Stack direction="row" spacing={1.25}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleEmailHrDirector}
                disabled={sending}
                data-testid="postif-email-hr"
              >
                {sending ? "Sending…" : "Email HR Director"}
              </Button>
              <Button variant="text" size="small" onClick={() => setPre("yes")}>
                I have approval
              </Button>
            </Stack>
          </Alert>
        )}

        {pre === "yes" && (
          <Box className="pif-start__lookup">
            <Box className="pif-start__step-row">
              <SectionDot done={!!matched} mark="2" />
              <Typography className="pif-start__step-question">Who are you hiring?</Typography>
              {detected && <Badge variant="primary" size="sm">{detected}</Badge>}
            </Box>
            <Box className="pif-start__lookup-field-row">
              <TextField
                value={q}
                onChange={handleQChange}
                onKeyDown={handleKeyDown}
                placeholder="Social Security Number or Employee ID"
                inputMode="numeric"
                autoComplete="off"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: primary action on the step
                autoFocus
                disabled={!!matched}
                data-testid="postif-lookup-input"
                inputProps={{ "aria-label": "Social Security Number or Employee ID" }}
                sx={{
                  flex: 1,
                  minWidth: 240,
                  "& .MuiOutlinedInput-input": {
                    fontSize: 14,
                    padding: "10px 13px",
                    letterSpacing: digits.length > 6 ? "0.04em" : undefined,
                  },
                }}
              />
            </Box>
            <Box className="pif-start__lookup-status">
              {(lookingUp || opening) && (
                <>
                  <CircularProgress size={14} thickness={5} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Looking up employee…
                  </Typography>
                </>
              )}
              {!lookingUp && !opening && !matched && !dnr && (
                <Typography variant="caption" color="text.secondary">
                  Enter a 9-digit SSN (formatted automatically as you type) or a 1–6 digit Employee ID. The employee
                  information will be retrieved automatically after you finish entering the value.
                </Typography>
              )}
            </Box>

            {notFound && attempts >= retryLimit && (
              <Alert severity="info" className="pif-start__alert">
                Still no match? Double-check the digits, or reach out to HR to confirm the record no need to keep
                guessing.
              </Alert>
            )}

            {dnr && (
              <Alert severity="error" className="pif-start__alert">
                <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                  This person can&apos;t be hired through this form
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {dnrMessage || "Please reach out to HR with any questions. We can't share more here."}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleContactHr}
                  disabled={contactingHr || dnrEmployeeId === null}
                  title={
                    dnrEmployeeId === null
                      ? "We can't identify this record well enough to email HR automatically - please contact HR directly."
                      : undefined
                  }
                >
                  {contactingHr ? "Sending…" : "Contact HR"}
                </Button>
              </Alert>
            )}

          </Box>
        )}
      </Box>

      <Box className="pif-start__rail">
        <Typography className="pif-start__rail-eyebrow">AFTER YOU SIGN</Typography>
        <Box className="pif-start__chain">
          {CHAIN_PREVIEW.map((c, i) => (
            <Box key={c.n} className="pif-start__chain-item">
              {i < CHAIN_PREVIEW.length - 1 && <Box className="pif-start__chain-line" />}
              <Box className="pif-start__chain-dot">{c.n}</Box>
              <Box>
                <Typography className="pif-start__chain-name">{c.name}</Typography>
                <Typography className="pif-start__chain-detail">{c.detail}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
        <Box className="pif-start__rail-note">
          Typically completed within 2–3 business days. A completed copy is returned to you automatically.
        </Box>
      </Box>

      {matched && (
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="pif-match-title"
          className="pif-start__match-overlay"
          style={{ ["--pif-match-flash-ms" as string]: `${MATCH_FLASH_MS}ms` }}
          data-testid="postif-match-flash"
        >
          <Box
            role="status"
            aria-live="polite"
            className="pif-start__match-card"
            onMouseEnter={pauseMatchFlash}
            onMouseLeave={resumeMatchFlash}
            onFocus={pauseMatchFlash}
            onBlur={handleMatchCardBlur}
          >
            <Typography id="pif-match-title" variant="caption" fontWeight={700} letterSpacing="0.12em" color="success.dark">
              MATCH FOUND · OPENING FORM{flashPaused ? " · PAUSED" : ""}
            </Typography>
            <Box className="pif-start__match-person">
              <Avatar
                src={matched.profileImage || undefined}
                alt={matchedName}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "secondary.main",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 18,
                  flex: "none",
                }}
              >
                {`${matched.first?.[0] ?? ""}${matched.last?.[0] ?? ""}`}
              </Avatar>
              <Box>
                <Typography className="pif-start__match-name">{matchedName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {matched.dob ? `Born ${matched.dob} · ` : ""}
                  {matched.employeeId ? `Employee ${matched.employeeId} · ` : ""}
                  {matched.status === "active" ? "currently employed" : "left the agency"}
                </Typography>
              </Box>
            </Box>
            <Box className="pif-start__match-footer">
              <Box className="pif-start__match-progress-wrap">
                <Box className="pif-start__match-progress-track">
                  <Box
                    className="pif-start__match-progress-bar"
                    style={{ animationPlayState: flashPaused ? "paused" : "running" }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Opening…
                </Typography>
              </Box>
              <Button variant="text" color="secondary" onClick={rejectMatched} data-testid="postif-match-wrong-person">
                Wrong person
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const SectionDot: FC<{ done: boolean; mark: string }> = ({ done, mark }) => (
  <Box className={`pif-start__section-dot${done ? " pif-start__section-dot--done" : ""}`}>
    {done ? "✓" : mark}
  </Box>
);

export default PifStartScreen;
